import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { apiKey } from "../config/config";
import { ToastContainer, toast } from "react-toastify";
import { io } from "socket.io-client";

function BranchChat({ lang, dirFormat }) {
  const isRtl = dirFormat === "rtl";
  const stored = JSON.parse(sessionStorage.getItem("aman-admin") || "null");
  const token = stored?.admin ? JSON.parse(stored.admin).token : null;
  const currentBranch = stored?.admin ? JSON.parse(stored.admin) : null;
  const currentBranchId = currentBranch?.id || currentBranch?.branch_id;

  const [messages, setMessages] = useState([]);
  // const [branches, setBranches] = useState([]);
  const [roomType,
    // setRoomType
  ] = useState("global"); // 'global' or 'city'
  const [optedOut, setOptedOut] = useState(false);
  const [messageText, setMessageText] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);

  const messagesEndRef = useRef(null);
  const socketRef = useRef(null);
  const inputRef = useRef(null);

  // Helper for translations
  const t = (key, fallback) => {
    return lang && lang[key] ? lang[key] : fallback;
  };

  // Fetch branches participating in chat
  // const fetchBranches = useCallback(() => {
  //   if (!token) return;
  //   axios
  //     .get(`${apiKey}/api/chat/branches`, {
  //       headers: { Authorization: `Bearer ${token}` },
  //     })
  //     .then((res) => {
  //       setBranches(res.data.result || []);
  //     })
  //     .catch((err) => {
  //       console.error("Error fetching branches:", err);
  //     });
  // }, [token]);

  // Fetch messages from backend
  const fetchMessages = useCallback(() => {
    if (!token) return;
    axios
      .get(`${apiKey}/chat/messages`, {
        params: {
          room_type: roomType,
          city: currentBranch?.city,
        },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setLoading(false);
        if (res.data.optedOut) {
          setOptedOut(true);
          setMessages([]);
        } else {
          setOptedOut(false);
          setMessages(res.data.result || []);
        }
      })
      .catch((err) => {
        setLoading(false);
        console.error("Error fetching messages:", err);
      });
  }, [token, roomType, currentBranch?.city]);

  // Handle opt-out preference update
  const handleToggleOptOut = (value) => {
    if (!token) return;
    const nextStatus = value ? 1 : 0;
    axios
      .put(
        `${apiKey}/chat/preference`,
        { chat_opt_out: nextStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then((res) => {
        setOptedOut(nextStatus === 1);
        if (nextStatus === 0) {
          toast.success(t("OptInSuccess", "Successfully joined the group chat"));
          fetchMessages();
        } else {
          toast.info(t("OptOutSuccess", "You have left the group chat"));
          setMessages([]);
        }
        // fetchBranches();
      })
      .catch((err) => {
        console.error("Error saving preference:", err);
        toast.error(t("PreferenceError", "Failed to update chat preference"));
      });
  };

  // Send message
  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!messageText.trim() || sending || !token) return;

    setSending(true);
    axios
      .post(
        `${apiKey}/chat/messages`,
        {
          message: messageText.trim(),
          room_type: roomType,
          city: roomType === "city" ? currentBranch?.city : null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then((res) => {
        setSending(false);
        setMessageText("");
        inputRef.current?.focus();
      })
      .catch((err) => {
        setSending(false);
        console.error("Error sending message:", err);
        toast.error(t("SendError", "Failed to send message"));
        inputRef.current?.focus();
      });
  };

  // Scroll to bottom helper
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Initial load
  useEffect(() => {
    // fetchBranches();
    fetchMessages();
  }, [fetchMessages]);

  // Socket.io connection and real-time updates setup
  useEffect(() => {
    if (optedOut || !token) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      return;
    }

    // Connect to backend socket server
    const socketUrl = apiKey ? apiKey.replace(/\/api$/, "") : "";
    const socket = io(socketUrl, {
      path: "/api/socket.io",
      // transports: ["polling"], // Polling uses standard HTTP POST/GET which Passenger supports
      // upgrade: false
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      console.log("Socket connected successfully");
      const targetRoom = roomType === "city" ? `city_${currentBranch?.city}` : "global";
      socket.emit("join_room", targetRoom);
    });

    socket.on("new_message", (newMsg) => {
      // Append message if it belongs to the active room type and criteria
      if (roomType === "global" && newMsg.room_type === "global") {
        setMessages((prev) => {
          if (prev.some((m) => m.id === newMsg.id)) return prev;
          return [...prev, newMsg];
        });
      } else if (
        roomType === "city" &&
        newMsg.room_type === "city" &&
        newMsg.city === currentBranch?.city
      ) {
        setMessages((prev) => {
          if (prev.some((m) => m.id === newMsg.id)) return prev;
          return [...prev, newMsg];
        });
      }
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [optedOut, token, roomType, currentBranch?.city]);

  // Polling branches list slower to keep participants list updated
  useEffect(() => {
    if (optedOut || !token) return;

    // const interval = setInterval(() => {
      // fetchBranches();
    // }, 12000); // Poll branch participants list every 12 seconds

    // return () => clearInterval(interval);
  }, [optedOut, token]);

  // Auto-scroll when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Group active vs opted-out branches
  // const activeBranches = branches.filter((b) => b.chat_opt_out === 0);
  // const optedOutBranches = branches.filter((b) => b.chat_opt_out === 1);

  return (
    <main id="main" className="main">
      <div className="pagetitle d-flex justify-content-between align-items-center">
        <div>
          <h1>{t("Branch_Chat", "Branch Chat")}</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">{t("General_Management", "General Management")}</li>
              <li className="breadcrumb-item active">{t("Branch_Chat", "Branch Chat")}</li>
            </ol>
          </nav>
        </div>

        {/* Opt-in/out toggle */}
        <div className="d-flex align-items-center bg-white border rounded shadow-sm px-3 py-2">
          <span className="me-2 fw-bold text-secondary" style={{ fontSize: "14px" }}>
            {optedOut
              ? t("ChatStatusOptedOut", "Chat Disabled (Opted Out)")
              : t("ChatStatusOptedIn", "Chat Enabled")}
          </span>
          <div className="form-check form-switch mb-0">
            <input
              className="form-check-input"
              type="checkbox"
              id="optOutToggle"
              style={{ width: "2.5em", height: "1.25em", cursor: "pointer" }}
              checked={!optedOut}
              onChange={(e) => handleToggleOptOut(!e.target.checked)}
            />
          </div>
        </div>
      </div>

      <section className="section">
        {/* <div className="row"> */}
        <div>
          {/* Sidebar Area: Room list and Branch list */}
          {/* <div className="col-lg-4 col-md-5">
            <div className="card shadow-sm mb-3">
              <div className="card-header bg-light py-3">
                <h5 className="card-title mb-0 p-0 fw-bold text-dark" style={{ fontSize: "16px" }}>
                  <i className="fa fa-comments me-2 text-primary"></i>
                  {t("Chat_Rooms", "Chat Rooms")}
                </h5>
              </div>
              <div className="card-body p-2">
                <button
                  className={`btn w-100 text-start d-flex justify-content-between align-items-center mb-2 p-3 border-0 ${
                    roomType === "global" ? "bg-primary text-white" : "bg-light text-dark"
                  }`}
                  style={{ borderRadius: "8px", transition: "all 0.2s" }}
                  onClick={() => setRoomType("global")}
                >
                  <div>
                    <i className="fa fa-globe me-2"></i>
                    <strong>{t("Global_Room", "All Branches Chat")}</strong>
                  </div>
                  <span className={`badge ${roomType === "global" ? "bg-white text-primary" : "bg-primary text-white"}`}>
                    {activeBranches.length}
                  </span>
                </button>

                <button
                  className={`btn w-100 text-start d-flex justify-content-between align-items-center p-3 border-0 ${
                    roomType === "city" ? "bg-primary text-white" : "bg-light text-dark"
                  }`}
                  style={{ borderRadius: "8px", transition: "all 0.2s" }}
                  onClick={() => setRoomType("city")}
                >
                  <div>
                    <i className="fa fa-map-marker me-2"></i>
                    <strong>
                      {t("City_Room", "City Group")} ({currentBranch?.city || "Local"})
                    </strong>
                  </div>
                  <span className={`badge ${roomType === "city" ? "bg-white text-primary" : "bg-primary text-white"}`}>
                    {activeBranches.filter((b) => b.city === currentBranch?.city).length}
                  </span>
                </button>
              </div>
            </div>

            <div className="card shadow-sm" style={{ maxHeight: "400px", display: "flex", flexDirection: "column" }}>
              <div className="card-header bg-light py-3">
                <h5 className="card-title mb-0 p-0 fw-bold text-dark" style={{ fontSize: "16px" }}>
                  <i className="fa fa-users me-2 text-primary"></i>
                  {t("Branches", "Branches")}
                </h5>
              </div>
              <div className="card-body p-0" style={{ overflowY: "auto", flex: 1 }}>
                <div className="px-3 py-2 bg-light border-bottom text-muted fw-bold" style={{ fontSize: "11px" }}>
                  {t("Active_Now", "ACTIVE PARTICIPANTS")} ({activeBranches.length})
                </div>
                {activeBranches.length === 0 ? (
                  <div className="p-3 text-muted text-center italic" style={{ fontSize: "13px" }}>
                    {t("NoActiveBranches", "No active branches")}
                  </div>
                ) : (
                  activeBranches.map((b) => (
                    <div
                      key={b.id}
                      className="d-flex align-items-center px-3 py-2 border-bottom hover-bg"
                      style={{
                        backgroundColor: b.id === currentBranchId ? "#f0f5ff" : "transparent",
                      }}
                    >
                      <img
                        src={b.branch_logo ? `${apiKey}/uploads/logos/${b.branch_logo}` : "/img/logo.png"}
                        alt="logo"
                        className="rounded-circle border me-3"
                        style={{ width: "38px", height: "38px", objectFit: "cover" }}
                        onError={(e) => {
                          e.target.src = "/img/logo.png";
                        }}
                      />
                      <div className="flex-grow-1 min-w-0">
                        <h6 className="mb-0 text-truncate text-dark fw-bold" style={{ fontSize: "14px" }}>
                          {b.customer_name}
                        </h6>
                        <span className="text-muted d-block text-truncate" style={{ fontSize: "11px" }}>
                          <i className="fa fa-map-pin me-1"></i>
                          {b.city} {b.id === currentBranchId ? `(${t("You", "You")})` : ""}
                        </span>
                      </div>
                      <span className="badge bg-success-light text-success ms-2" style={{ fontSize: "10px" }}>
                        ●
                      </span>
                    </div>
                  ))
                )}

                {optedOutBranches.length > 0 && (
                  <>
                    <div className="px-3 py-2 bg-light border-bottom text-muted fw-bold" style={{ fontSize: "11px" }}>
                      {t("OptedOutBranches", "OPTED OUT")} ({optedOutBranches.length})
                    </div>
                    {optedOutBranches.map((b) => (
                      <div key={b.id} className="d-flex align-items-center px-3 py-2 border-bottom opacity-75">
                        <img
                          src={b.branch_logo ? `${apiKey}/uploads/logos/${b.branch_logo}` : "/img/logo.png"}
                          alt="logo"
                          className="rounded-circle border me-3 grayscale"
                          style={{ width: "34px", height: "34px", objectFit: "cover" }}
                          onError={(e) => {
                            e.target.src = "/img/logo.png";
                          }}
                        />
                        <div className="flex-grow-1 min-w-0">
                          <h6 className="mb-0 text-truncate text-muted" style={{ fontSize: "13px" }}>
                            {b.customer_name}
                          </h6>
                          <span className="text-muted d-block text-truncate" style={{ fontSize: "11px" }}>
                            {b.city}
                          </span>
                        </div>
                        <span className="badge bg-secondary-light text-secondary ms-2" style={{ fontSize: "10px" }}>
                          {t("OptedOut", "Offline")}
                        </span>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
          </div> */}

          {/* Main Chat Feed Area */}
          <div className="">
            <div className="card shadow-sm d-flex flex-column p-1" style={{ height: "80vh" }}>
              {/* Header */}
              <div className="card-header bg-white border-bottom py-1 d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <div className="bg-primary-light p-2 rounded-circle me-3 d-flex align-items-center justify-content-center" style={{ width: "40px", height: "40px" }}>
                    <i className={`fa ${roomType === "global" ? "fa-globe text-primary" : "fa-map-marker text-primary"} fs-5`}></i>
                  </div>
                  <div>
                    <h5 className="mb-0 fw-bold text-dark" style={{ fontSize: "16px" }}>
                      {roomType === "global"
                        ? t("AllBranchesGroupChat", "Offices Chat")
                        : `${t("CityChat", "Office Group Chat")} - ${currentBranch?.city || ""}`}
                    </h5>
                    {/* <small className="text-muted">
                      {activeBranches.length} {t("branches_participating", "branches active")}
                    </small> */}
                  </div>
                </div>
              </div>

              {/* Feed */}
              <div className="card-body p-4 flex-grow-1" style={{ overflowY: "auto", backgroundColor: "#f8f9fa" }}>
                {optedOut ? (
                  <div className="h-100 d-flex flex-column align-items-center justify-content-center text-center p-4">
                    <div className="bg-warning-light p-4 rounded-circle mb-3">
                      <i className="fa-regular fa-comments text-warning fs-1"></i>
                    </div>
                    <h5 className="fw-bold">{t("ChatDisabledTitle", "Chat Feed Disabled")}</h5>
                    <p className="text-muted max-w-sm" style={{ maxWidth: "340px", fontSize: "14px" }}>
                      {t("ChatDisabledText", "You have opted out of the branch chat. Enable the toggle in the upper right to rejoin the chat and communicate with other branches.")}
                    </p>
                    <button className="btn btn-primary px-4 mt-2" onClick={() => handleToggleOptOut(false)}>
                      <i className="fa fa-plug me-2"></i>
                      {t("JoinChatButton", "Join Chat Now")}
                    </button>
                  </div>
                ) : loading ? (
                  <div className="h-100 d-flex align-items-center justify-content-center">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : messages.length === 0 ? (
                  <div className="h-100 d-flex flex-column align-items-center justify-content-center text-muted">
                    <i className="fa-regular fa-message fs-1 mb-2"></i>
                    <p style={{ fontSize: "15px" }}>{t("NoMessagesYet", "No messages in this chat yet. Start the conversation!")}</p>
                  </div>
                ) : (
                  messages.map((msg, index) => {
                    const isOwnMessage = msg.sender_id === currentBranchId;
                    return (
                      <div
                        key={msg.id || index}
                        className={`d-flex mb-3 ${isOwnMessage ? "justify-content-end" : "justify-content-start"}`}
                      >
                        {/* Sender Logo (only on other people's messages) */}
                        {!isOwnMessage && (
                          <img
                            src={msg.sender_logo ? `${apiKey}/uploads/logos/${msg.sender_logo}` : "/img/logo.png"}
                            alt="logo"
                            className="rounded-circle border me-2 mt-1"
                            style={{ width: "32px", height: "32px", objectFit: "cover" }}
                            onError={(e) => {
                              e.target.src = "/img/logo.png";
                            }}
                          />
                        )}

                        <div style={{ maxWidth: "75%" }}>
                          {/* Sender name / metadata */}
                          {!isOwnMessage && (
                            <div className="d-flex align-items-center mb-1 px-1">
                              <span className="fw-bold text-dark me-2" style={{ fontSize: "12px" }}>
                                {msg.sender_name}
                              </span>
                              <span className="badge bg-secondary-light text-secondary" style={{ fontSize: "9px" }}>
                                {msg.sender_city}
                              </span>
                            </div>
                          )}

                          {/* Text bubble */}
                          <div
                            className={`p-3 text-break shadow-sm ${isOwnMessage
                                ? "bg-primary text-white rounded-start-lg rounded-top-lg"
                                : "bg-white text-dark rounded-end-lg rounded-top-lg"
                              }`}
                            style={{
                              borderRadius: "16px",
                              borderTopLeftRadius: isOwnMessage ? "16px" : "4px",
                              borderTopRightRadius: isOwnMessage ? "4px" : "16px",
                              fontSize: "14px",
                              lineHeight: "1.4",
                            }}
                          >
                            <div style={{ whiteSpace: "pre-wrap" }}>{msg.message}</div>
                            <div
                              className={`text-end mt-1 ${isOwnMessage ? "text-white-50" : "text-muted"}`}
                              style={{ fontSize: "10px" }}
                            >
                              {new Date(msg.created_at).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input Bar */}
              <div className="card-footer bg-white border-top p-1">
                <form onSubmit={handleSendMessage} className="d-flex align-items-center m-0">
                  <input
                    type="text"
                    ref={inputRef}
                    className="form-control me-2 py-2"
                    placeholder={
                      optedOut
                        ? t("InputPlaceholderOptedOut", "Chat is disabled")
                        : t("InputPlaceholderActive", "Type your message here...")
                    }
                    style={{ borderRadius: "20px" }}
                    disabled={optedOut}
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                  />
                  <button
                    type="submit"
                    className="btn btn-primary d-flex align-items-center justify-content-center p-0 rounded-circle"
                    style={{ width: "42px", height: "42px", flexShrink: 0 }}
                    disabled={optedOut || sending || !messageText.trim()}
                  >
                    {sending ? (
                      <span className="spinner-border spinner-border-sm" role="status"></span>
                    ) : (
                      <i className={`fa fa-paper-plane ${isRtl ? "fa-rotate-180" : ""}`} style={{ fontSize: "16px" }}></i>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />
    </main>
  );
}

export default BranchChat;
